import { Breadcrumb, Button, Col, List, Row, Space, Tabs } from 'antd';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, Link, useParams, useSearchParams } from 'react-router';

import { PATHS } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { Layout } from '@/shared/ui';

import { authModel } from '@/entities/auth';
import { organizationConfig } from '@/entities/organization';
import { userModel } from '@/entities/user';

import { Account } from '@/widgets/account';
import { UserSecurity, userSecurityMessages } from '@/widgets/userSecurity';

import { CreateOrganizationModal } from './ui/CreateOrganizationModal/CreateOrganizationModal';
import styles from './User.module.scss';

export const User: FC = () => {
  const { userID } = useParams<{ userID: string }>();

  const { formatMessage } = useIntl();

  const [searchParams, setSearchParams] = useSearchParams();

  const [isCreateOrganizationModalOpen, setIsCreateOrganizationModalOpen] =
    useState<boolean>(false);

  const currentUserStaff = useAppSelector(
    userModel.selectors.selectSortedCurrentUserStaff,
  );
  const organizationNames = useAppSelector(
    userModel.selectors.selectCurrentUserOrganizationNames,
  );
  const sharedFromOrganizations = useAppSelector(
    userModel.selectors.selectAllSharedFromOrganizations,
  )
    .slice()
    .sort(
      (a, b) =>
        (b.AcceptedSharingCount > 0 ? 1 : 0) -
        (a.AcceptedSharingCount > 0 ? 1 : 0),
    );
  const currentUser = useAppSelector(userModel.selectors.selectCurrentUser);
  const hasAdminBasicAccess = useAppSelector(
    authModel.selectors.selectHasAdminBasicAccess,
  );
  const canManageDSO = useAppSelector(userModel.selectors.selectCanManageDSO);
  const canManageDistributors = useAppSelector(
    userModel.selectors.selectCanManageDistributors,
  );
  const canViewMFA = useAppSelector(authModel.selectors.selectCanViewMFA);

  const tabParam = searchParams.get('tab');
  const activeTab =
    tabParam === 'security' && canViewMFA ? tabParam : 'overview';

  const handleTabChange = (key: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', key);
    setSearchParams(nextParams, { replace: true });
  };

  const openCreateOrganizationModal = () =>
    setIsCreateOrganizationModalOpen(true);
  const closeCreateOrganizationModal = () =>
    setIsCreateOrganizationModalOpen(false);

  return (
    <Layout.Content>
      <Breadcrumb
        style={{ padding: '24px 0' }}
        className="p2"
        items={[
          {
            title: (
              <Link className={styles.breadcrumb} to={generatePath(PATHS.main)}>
                {formatMessage({
                  id: 'breadcrumb.home',
                  defaultMessage: 'Home',
                })}
              </Link>
            ),
          },
          {
            title: `${currentUser?.PersonalData?.FirstName ?? ''} ${
              currentUser?.PersonalData?.LastName ?? ''
            }`,
          },
        ]}
      />

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'overview',
            label: formatMessage({
              id: 'user.tabs.overview',
              defaultMessage: 'Overview',
            }),
            children: (
              <Row gutter={16} style={{ rowGap: 24 }}>
                <Col md={16} lg={8}>
                  <Space
                    direction="vertical"
                    size="large"
                    className={styles.space}
                  >
                    <Account userID={userID} />
                  </Space>
                </Col>

                <Col md={32} lg={16} style={{ gap: 24 }}>
                  <Space
                    direction="vertical"
                    size="small"
                    className={styles.space}
                  >
                    {/* TODO: replace via table with subscription name and status */}
                    <List
                      size="small"
                      header={
                        <div>
                          {formatMessage({
                            id: 'user.organizationsListTitle',
                            defaultMessage: 'Organizations',
                          })}
                        </div>
                      }
                      bordered
                      dataSource={currentUserStaff}
                      renderItem={(staff) => {
                        const status =
                          userModel.selectors.getStaffStatus(staff);
                        const isBlocked = status !== 'active';
                        const roleLabel = formatMessage(
                          organizationConfig.staffRoleMessages[staff.Role],
                        );
                        const orgName =
                          organizationNames[staff.OrganizationID] ||
                          staff.OrganizationID;

                        const content = (
                          <>
                            {orgName}
                            {roleLabel.trim() && (
                              <span className={styles.organizationRole}>
                                : {roleLabel}
                              </span>
                            )}
                            {status !== 'active' && (
                              <span className={styles.organizationStatus}>
                                ({status})
                              </span>
                            )}
                          </>
                        );

                        return (
                          <List.Item>
                            {isBlocked ? (
                              <span className={styles.organizationDisabled}>
                                {content}
                              </span>
                            ) : (
                              <Link
                                className={styles.organizationLink}
                                to={generatePath(PATHS.organization, {
                                  organizationID: staff.OrganizationID,
                                })}
                              >
                                {content}
                              </Link>
                            )}
                          </List.Item>
                        );
                      }}
                    />

                    {hasAdminBasicAccess && (
                      <Button
                        type="primary"
                        onClick={openCreateOrganizationModal}
                      >
                        {formatMessage({
                          id: 'organization.button.addOrganization',
                          defaultMessage: 'Add organization',
                        })}
                      </Button>
                    )}

                    <List
                      size="small"
                      loading={!currentUser}
                      header={
                        <div>
                          {formatMessage({
                            id: 'user.sharedFromOrganizationsListTitle',
                            defaultMessage: 'Shared from',
                          })}
                        </div>
                      }
                      bordered
                      dataSource={sharedFromOrganizations}
                      renderItem={(org) => {
                        const isBlocked = org.AcceptedSharingCount === 0;
                        return (
                          <List.Item>
                            {isBlocked ? (
                              <span className={styles.organizationDisabled}>
                                {org.OrganizationName || org.OrganizationID}
                              </span>
                            ) : (
                              <Link
                                className={styles.organizationLink}
                                to={generatePath(PATHS.organization, {
                                  organizationID: org.OrganizationID,
                                })}
                              >
                                {org.OrganizationName || org.OrganizationID}
                              </Link>
                            )}
                            {`: accepted: ${org.AcceptedSharingCount}, pending: ${org.PendingSharingCount}, canceled: ${org.CanceledSharingCount}`}
                          </List.Item>
                        );
                      }}
                    />
                  </Space>
                </Col>
              </Row>
            ),
          },
          ...(canViewMFA
            ? [
                {
                  key: 'security',
                  label: formatMessage(userSecurityMessages.title),
                  children: <UserSecurity userID={userID} />,
                },
              ]
            : []),
        ]}
      />

      <CreateOrganizationModal
        isOpen={isCreateOrganizationModalOpen}
        canManageDSO={canManageDSO}
        canManageDistributors={canManageDistributors}
        onCloseHandler={closeCreateOrganizationModal}
      />
    </Layout.Content>
  );
};
